/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/context/LexiconIntlProvider.tsx
 *
 * Rôle :
 *   Monte `<IntlProvider>` avec un catalogue DÉJÀ passé au lexique de
 *   l'événement actif : « atelier » devient « activité » sur un forum des
 *   métiers, « session » sur un séminaire, etc.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI ICI, ET PAS DANS LES 112 SITES D'APPEL, c'est le point de fiabilité
 * ─────────────────────────────────────────────────────────────────────────
 *   L'autre voie possible était de laisser les tokens `{lexWorkshop…}` dans le
 *   catalogue et de compter sur `values={…}` à chaque `<FormattedMessage>`.
 *   Elle est FRAGILE : un seul écran qui oublie de passer `values` affiche
 *   « {lexWorkshopPlural} » à l'utilisateur, et TypeScript n'en sait rien
 *   `values` est optionnel par signature. 112 chaînes réparties dans des
 *   dizaines de fichiers, c'est 112 occasions d'oublier, plus une à chaque
 *   nouvel écran écrit plus tard par quelqu'un qui ignore la règle.
 *
 *   En substituant AU-DESSUS de react-intl, le catalogue que reçoit
 *   `IntlProvider` ne contient plus aucun token de lexique. Un écran ne PEUT
 *   PAS se tromper : il n'a rien à faire. La garantie est structurelle, pas
 *   disciplinaire.
 *
 * Ordre des providers (attention si vous réorganisez `main.tsx`) :
 *   ce composant lit `useEventConfig()`, qui repose sur react-query. Il doit
 *   donc vivre SOUS `<QueryClientProvider>`, alors que `IntlProvider` était
 *   historiquement au-dessus. L'inversion est sans effet : rien entre les deux
 *   ne consomme react-intl.
 *
 * Zéro régression :
 *   `useEventConfig()` ne renvoie jamais null (repli DEFAULT_EVENT_CONFIG), et
 *   le lexique par défaut est le vocabulaire historique. Avant la réponse du
 *   serveur, pendant une panne réseau, ou pour un événement sans lexique
 *   configuré, les chaînes rendues sont EXACTEMENT celles d'avant ce lot.
 *
 * Réutilisable : 100 %, aucune dépendance à NOLAS ni à CIRFA.
 *
 * Voir : utils/eventLexicon.ts (le moteur), main.tsx (montage).
 *
 * @author Mikael
 * @since  2026-08-03 (lot CIRFA, lexique configurable)
 */

import { useMemo, type ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { useEventConfig } from '@/hooks/useEventConfig';
import {
  applyLexiconToMessages,
  lexiconTokens,
  resolveEventLexicon,
} from '@/utils/eventLexicon';
import { messagesFr } from '@/i18n/fr';
import { applyIdentityToMessages } from '@/i18n/applyIdentityTokens';
import { computeEventIdentity } from '@/context/EventIdentityProvider';

interface LexiconIntlProviderProps {
  readonly children: ReactNode;
}

/**
 * `IntlProvider` dont le catalogue est interpolé par le lexique de l'événement.
 *
 * Coût : la substitution parcourt le catalogue une fois par changement de
 * lexique, c'est-à-dire une fois au démarrage, puis seulement si un admin
 * modifie le vocabulaire. `useMemo` est indexé sur les valeurs résolues (et non
 * sur l'objet `event`, qui change d'identité à chaque revalidation react-query) :
 * une revalidation qui ne modifie pas le vocabulaire ne recalcule rien.
 */
export function LexiconIntlProvider({ children }: LexiconIntlProviderProps): JSX.Element {
  const { event } = useEventConfig();

  const wording = event.branding?.wording;
  const eventType = event.type;

  const tokens = useMemo(
    () => lexiconTokens(resolveEventLexicon(wording, eventType)),
    [wording, eventType],
  );

  // Clé de mémoïsation STABLE : la concaténation des formes résolues. Deux
  // objets `wording` différents mais équivalents ne provoquent pas de recalcul
  // du catalogue entier.
  const tokensKey = useMemo(() => JSON.stringify(tokens), [tokens]);

  // ── Jetons d'IDENTITE ({eventName}, {eventTypeAt}...) ──────────────────
  //
  // Ajoutes le 2026-09-09. Le vocabulaire {lex*} etait substitue ici, donc de
  // facon structurelle ; l'identite, elle, reposait sur la discipline de
  // chaque site d'appel (`values={identity}`). L'oubli a fini par arriver, et
  // se lisait sur une page PUBLIQUE : la carte d'animation du choix de profil
  // affichait « Vous animez un atelier {eventTypeAt} {eventName}. », jetons
  // bruts compris, parce que le rendu des cartes n'attache aucun `values`.
  //
  // Reparer ce seul site d'appel aurait laisse la cause en place. Les deux
  // familles de jetons sont desormais traitees pareil : un ecran n'a plus rien
  // a ne pas oublier. Les `values={identity}` deja ecrits ailleurs restent
  // sans effet nefaste (react-intl ignore une valeur sans jeton correspondant).
  //
  // Mikael - chef de projet - 2026-09-09
  const identite = useMemo(() => computeEventIdentity(event), [event]);
  const identiteCle = useMemo(() => JSON.stringify(identite), [identite]);

  const messages = useMemo(
    () => applyIdentityToMessages(applyLexiconToMessages(messagesFr, tokens), identite),
    // `tokens` est dérivé de `tokensKey`, `identite` de `identiteCle` : ce sont
    // bien les clés qui font autorité (elles évitent de recalculer le catalogue
    // à chaque revalidation react-query qui ne change rien).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cf. commentaire ci-dessus
    [tokensKey, identiteCle],
  );

  return (
    <IntlProvider locale="fr-FR" defaultLocale="fr-FR" messages={messages}>
      {children}
    </IntlProvider>
  );
}
